import { useState, useEffect } from 'react'

// Sistema de breakpoints responsivos
export const BREAKPOINTS = {
  xs: '320px',   // Smartphones pequenos
  sm: '640px',   // Smartphones grandes
  md: '768px',   // Tablets
  lg: '1024px',  // Laptops pequenos
  xl: '1280px',  // Laptops grandes
  '2xl': '1536px' // Desktops grandes
}

// Hook para detectar tamanho da tela
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState('lg')
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [isDesktop, setIsDesktop] = useState(true)

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth
      
      if (width < 640) {
        setScreenSize('xs')
        setIsMobile(true)
        setIsTablet(false)
        setIsDesktop(false)
      } else if (width < 768) {
        setScreenSize('sm')
        setIsMobile(true)
        setIsTablet(false)
        setIsDesktop(false)
      } else if (width < 1024) {
        setScreenSize('md')
        setIsMobile(false)
        setIsTablet(true)
        setIsDesktop(false)
      } else if (width < 1280) {
        setScreenSize('lg')
        setIsMobile(false)
        setIsTablet(false)
        setIsDesktop(true)
      } else {
        setScreenSize('xl')
        setIsMobile(false)
        setIsTablet(false)
        setIsDesktop(true)
      }
    }

    updateScreenSize()
    window.addEventListener('resize', updateScreenSize)
    
    return () => window.removeEventListener('resize', updateScreenSize)
  }, [])

  return {
    screenSize,
    isMobile,
    isTablet,
    isDesktop,
    isSmallScreen: isMobile || isTablet
  }
}

// Hook para orientação do dispositivo
export const useOrientation = () => {
  const [orientation, setOrientation] = useState('portrait')

  useEffect(() => {
    const updateOrientation = () => {
      setOrientation(window.innerHeight > window.innerWidth ? 'portrait' : 'landscape')
    }

    updateOrientation()
    window.addEventListener('resize', updateOrientation)
    window.addEventListener('orientationchange', updateOrientation)
    
    return () => {
      window.removeEventListener('resize', updateOrientation)
      window.removeEventListener('orientationchange', updateOrientation)
    }
  }, [])

  return {
    orientation,
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape'
  }
}

// Componente de container responsivo
export const ResponsiveContainer = ({ children, className = '', maxWidth = '6xl' }) => {
  const { isMobile } = useResponsive()
  
  return (
    <div className={`mx-auto px-4 sm:px-6 lg:px-8 max-w-${maxWidth} ${className}`}>
      {children}
    </div>
  )
}

// Componente de grid responsivo
export const ResponsiveGrid = ({ 
  children, 
  cols = { xs: 1, sm: 2, md: 3, lg: 4 },
  gap = 4,
  className = ''
}) => {
  const gridClasses = `
    grid 
    grid-cols-${cols.xs} 
    sm:grid-cols-${cols.sm} 
    md:grid-cols-${cols.md} 
    lg:grid-cols-${cols.lg}
    gap-${gap}
    ${className}
  `
  
  return <div className={gridClasses}>{children}</div>
}

// Componente de card responsivo
export const ResponsiveCard = ({ 
  children, 
  className = '',
  padding = { xs: 4, sm: 6 },
  shadow = true 
}) => {
  const cardClasses = `
    bg-white rounded-xl
    p-${padding.xs} sm:p-${padding.sm}
    ${shadow ? 'shadow-lg' : 'shadow-sm'}
    ${className}
  `
  
  return <div className={cardClasses}>{children}</div>
}

// Hook para detectar se é touch device
export const useTouchDevice = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  useEffect(() => {
    setIsTouchDevice(
      'ontouchstart' in window || 
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches
    )
  }, [])

  return isTouchDevice
}

// Hook para detectar se está em modo PWA
export const usePWA = () => {
  const [isPWA, setIsPWA] = useState(false)

  useEffect(() => {
    const checkPWA = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      const isIOSStandalone = window.navigator.standalone === true
      setIsPWA(isStandalone || isIOSStandalone)
    }

    checkPWA()
    window.matchMedia('(display-mode: standalone)').addEventListener('change', checkPWA)
    
    return () => {
      window.matchMedia('(display-mode: standalone)').removeEventListener('change', checkPWA)
    }
  }, [])

  return isPWA
}

// Utilitários para classes responsivas
export const responsiveClasses = {
  // Padding responsivo
  padding: {
    xs: 'p-2',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  },
  
  // Margin responsivo
  margin: {
    xs: 'm-2',
    sm: 'm-4',
    md: 'm-6',
    lg: 'm-8'
  },
  
  // Texto responsivo
  text: {
    xs: 'text-sm',
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-xl'
  },
  
  // Espaçamento responsivo
  spacing: {
    xs: 'space-y-2',
    sm: 'space-y-4',
    md: 'space-y-6',
    lg: 'space-y-8'
  }
}

// Hook para gerar classes responsivas
export const useResponsiveClasses = (baseClasses = '') => {
  const { isMobile, isTablet, isDesktop } = useResponsive()
  
  const getResponsiveClasses = (classes) => {
    if (isMobile) return classes.mobile || classes.default || ''
    if (isTablet) return classes.tablet || classes.default || ''
    if (isDesktop) return classes.desktop || classes.default || ''
    return classes.default || ''
  }
  
  return {
    getResponsiveClasses,
    isMobile,
    isTablet,
    isDesktop,
    baseClasses
  }
}

// Componente de botão responsivo
export const ResponsiveButton = ({ 
  children, 
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  ...props 
}) => {
  const { isMobile } = useResponsive()
  
  const sizeClasses = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  }
  
  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    success: 'bg-green-600 text-white hover:bg-green-700',
    outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
  }
  
  const buttonClasses = `
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${fullWidth || isMobile ? 'w-full' : 'w-auto'}
    rounded-lg font-medium transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    ${className}
  `
  
  return (
    <button className={buttonClasses} {...props}>
      {children}
    </button>
  )
}

// Componente de input responsivo
export const ResponsiveInput = ({ 
  type = 'text',
  placeholder = '',
  fullWidth = true,
  className = '',
  ...props 
}) => {
  const inputClasses = `
    ${fullWidth ? 'w-full' : 'w-auto'}
    px-3 py-2 border border-gray-300 rounded-lg
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
    text-sm sm:text-base
    ${className}
  `
  
  return (
    <input
      type={type}
      placeholder={placeholder}
      className={inputClasses}
      {...props}
    />
  )
}

// Hook para detectar se deve mostrar sidebar
export const useSidebarVisibility = () => {
  const { isMobile, isTablet } = useResponsive()
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile)
  
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false)
    } else {
      setSidebarOpen(true)
    }
  }, [isMobile, isTablet])
  
  return {
    sidebarOpen,
    setSidebarOpen,
    shouldShowSidebar: !isMobile,
    shouldShowMobileMenu: isMobile
  }
}

export default {
  BREAKPOINTS,
  useResponsive,
  useOrientation,
  useTouchDevice,
  usePWA,
  useResponsiveClasses,
  useSidebarVisibility,
  ResponsiveContainer,
  ResponsiveGrid,
  ResponsiveCard,
  ResponsiveButton,
  ResponsiveInput,
  responsiveClasses
}
